import i18n from "../../../i18n";import { Trans } from "react-i18next";import { useEffect, useMemo, useRef, useState } from "react";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { CalendarIcon, ChevronDown, X } from "lucide-react";
import { format } from "date-fns";
import { Button } from "../../../components/ui/button";
import {
  Dialog,
  DialogOverlay,
  DialogPortal,
  DialogTitle } from
"../../../components/ui/dialog";
import { Calendar } from "../../../components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger } from
"../../../components/ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue } from
"../../../components/ui/select";
import { Textarea } from "../../../components/ui/textarea";
import { cn } from "../../../lib/utils";
import {
  fetchDepartments,
  fetchHoliday,
  fetchWorkLocations } from
"../../../services/holidayService";

function CustomTimeSelect({ value, onChange, options }) {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const selectedLabel = options.find((o) => o.value === value)?.label || "";

  return (
    <div ref={containerRef} className="relative w-full">
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="w-full h-10 px-3 rounded-xl border border-[var(--color-text-border)] bg-white text-sm flex items-center justify-between cursor-pointer">
        
        <span>{selectedLabel}</span>
        <ChevronDown className="h-4 w-4 text-muted-foreground" />
      </button>
      {isOpen &&
      <div className="absolute top-full left-0 w-full max-h-[200px] overflow-y-auto bg-white border border-[var(--color-text-border)] rounded-xl shadow-md z-10 mt-1">
          {options.map((t) =>
        <button
          type="button"
          key={t.value}
          onClick={() => {
            onChange(t.value);
            setIsOpen(false);
          }}
          className={cn(
            "w-full text-left px-3 py-2 text-sm cursor-pointer",
            value === t.value ?
            "bg-[var(--color-primary)] text-white" :
            "text-foreground hover:bg-[var(--color-workspace)]"
          )}>
          
              {t.label}
            </button>
        )}
        </div>
      }
    </div>);

}

const fieldLabelClass =
"block text-xs font-semibold text-[var(--color-text-secondary)] mb-1.5";
const inputClass =
"w-full h-10 px-3 rounded-xl border bg-white text-sm outline-none focus:ring-1 focus:ring-[var(--color-accent)]";

export function CreateEventDialog({
  isPanelOpen,
  setIsPanelOpen,
  formState,
  setFormState,
  fieldErrors,
  setFieldErrors,
  showErrorSummary,
  validateField,
  handleSaveSubmit,
  isFormValid,
  triggerToast
}) {
  const timeOptions = useMemo(() => {
    const times = [];
    for (let h = 0; h < 24; h++) {
      for (let m = 0; m < 60; m += 15) {
        const ampm = h >= 12 ? "pm" : "am";
        const displayH = h % 12 === 0 ? 12 : h % 12;
        const displayM = m.toString().padStart(2, "0");
        const valueH = h.toString().padStart(2, "0");
        times.push({
          label: `${displayH}:${displayM}${ampm}`,
          value: `${valueH}:${displayM}`
        });
      }
    }
    return times;
  }, []);

  const [locations, setLocations] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [holidayTypes, setHolidayTypes] = useState([]);

  useEffect(() => {
    fetchHoliday()
    .then((data) => setHolidayTypes(Array.isArray(data) ? data : data?.data ?? []))
    .catch((error) => {
      setHolidayTypes([]);
      triggerToast(error.message || "Failed to load holiday types.", "error");
    });
  }, [triggerToast]);

  useEffect(() => {
    if (!isPanelOpen) return;
    if (formState.visibility === "Work Location" && locations.length === 0) {
      fetchWorkLocations()
      .then((data) => setLocations(Array.isArray(data) ? data : data?.data ?? []))
      .catch((error) => {
        setLocations([]);
        triggerToast(error.message || "Failed to load location.", "error");
      });
    }
    if (formState.visibility === "Department" && departments.length === 0) {
      fetchDepartments()
      .then((data) => setDepartments(Array.isArray(data) ? data : data?.data ?? []))
      .catch((error) => {
        setDepartments([]);
        triggerToast(error.message || "Failed to load department.", "error");
      });
    }
  }, [isPanelOpen, formState.visibility, locations.length, departments.length, triggerToast]);

  const todayMin = new Date().toISOString().split("T")[0];

  return (
    <Dialog open={isPanelOpen} onOpenChange={setIsPanelOpen}>
      <DialogPortal>
        <DialogOverlay className="bg-[#0a1230]/50 backdrop-blur-sm" />
        <DialogPrimitive.Content
          className={cn(
            "fixed left-[50%] top-[50%] z-50 translate-x-[-50%] translate-y-[-50%]",
            "w-[95vw] sm:w-[570px] max-w-[570px] max-h-[90vh] overflow-hidden flex flex-col",
            "bg-white rounded-2xl shadow-[0_30px_80px_-20px_rgba(30,51,131,0.45)] border border-white/60",
            "font-sans duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95"
          )}>
          
          <div className="flex items-center justify-between px-6 py-4 border-b border-[var(--color-text-border)]/70 shrink-0">
            <DialogTitle className="text-[18px] font-bold text-foreground tracking-tight">
              {formState.id ? "Edit Event" : "Create Holiday/Event"}
            </DialogTitle>
            <button
              type="button"
              onClick={() => setIsPanelOpen(false)}
              className="h-8 w-8 rounded-lg grid place-items-center text-muted-foreground hover:bg-[var(--color-workspace)]"
              aria-label={i18n.t("formActions.close")}>
              
              <X className="h-4 w-4" />
            </button>
          </div>

          <form
            onSubmit={handleSaveSubmit}
            className="flex-1 overflow-y-auto px-6 py-5 space-y-5">
            
            {showErrorSummary && Object.keys(fieldErrors).length > 0 &&
            <div className="bg-[var(--color-error)]/10 border border-[var(--color-error)]/25 rounded-xl px-4 py-3">
                <ul className="m-0 pl-5 text-xs text-[var(--color-error)] space-y-1">
                  {Object.values(fieldErrors).map((err, i) =>
                <li key={i}>{err}</li>
                )}
                </ul>
              </div>
            }

            <div>
              <label className={fieldLabelClass}>
                <Trans i18nKey="dateTime.selectHolidayType" /> <span className="text-[var(--color-error)]">*</span>
              </label>
              <Select
                value={formState.type || undefined}
                onValueChange={(value) =>
                setFormState((p) => ({ ...p, type: value }))
                }>
                
                <SelectTrigger className="h-10 rounded-xl border-[var(--color-text-border)] bg-white">
                  <SelectValue placeholder={i18n.t("dateTime.selectHolidayType")} />
                </SelectTrigger>
                <SelectContent>
                  {holidayTypes.map((cat) =>
                  <SelectItem key={cat.categoryId} value={cat.categoryName}>
                      {cat.categoryName}
                    </SelectItem>
                  )}
                </SelectContent>
              </Select>
              {formState.type === "Company Event" &&
              <p className="mt-1 text-[11px] text-muted-foreground">
                  <Trans i18nKey="holidays.informationalOnlyDoesNotAutoBlockLogicMetrics" />
                </p>
              }
            </div>

            <div>
              <label className={fieldLabelClass}>
                <Trans i18nKey="holidays.eventTitle" /> <span className="text-[var(--color-error)]">*</span>
              </label>
              <input
                value={formState.title}
                onChange={(e) =>
                setFormState((p) => ({ ...p, title: e.target.value }))
                }
                onBlur={(e) => validateField("title", e.target.value)}
                placeholder={i18n.t("examples.eGIndependenceDay")}
                className={cn(
                  inputClass,
                  fieldErrors.title ?
                  "border-[var(--color-error)]" :
                  "border-[var(--color-text-border)]"
                )} />
              
              {fieldErrors.title &&
              <p className="text-[11px] text-[var(--color-error)] mt-1">
                  {fieldErrors.title}
                </p>
              }
            </div>

            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={() =>
                setFormState((p) => ({ ...p, allDay: !p.allDay }))
                }
                className={cn(
                  "relative w-10 h-[22px] rounded-full transition-colors shrink-0",
                  formState.allDay ? "bg-[var(--color-primary)]" : "bg-gray-200"
                )}
                aria-pressed={formState.allDay}>
                
                <span
                  className={cn(
                    "absolute top-0.5 h-[18px] w-[18px] rounded-full bg-white shadow transition-all",
                    formState.allDay ? "left-5" : "left-0.5"
                  )} />
                
              </button>
              <span className="text-sm font-semibold text-foreground">
                <Trans i18nKey="dateTime.allDayEvent" />
              </span>
            </div>

            <div className="flex gap-3">
              <div className="flex-1">
                <label className={fieldLabelClass}>
                  <Trans i18nKey="dateTime.startDate" /> <span className="text-[var(--color-error)]">*</span>
                </label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      type="button"
                      variant="outline"
                      className={cn(
                        "w-full h-10 justify-start text-left font-normal rounded-xl border bg-white px-3 text-sm hover:bg-[var(--color-workspace)]/40",
                        !formState.start && "text-muted-foreground",
                        fieldErrors.start ?
                        "border-[var(--color-error)]" :
                        "border-[var(--color-text-border)]"
                      )}>
                      
                      <CalendarIcon className="mr-2 h-4 w-4 text-[var(--color-text-secondary)]" />
                      {formState.start ?
                      format(new Date(formState.start), "PPP") :

                      <span><Trans i18nKey="dateTime.pickADate" /></span>
                      }
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto border-0 bg-transparent p-0 shadow-none" align="start">
                    <Calendar
                      mode="single"
                      selected={formState.start ? new Date(formState.start) : undefined}
                      disabled={{ before: new Date(todayMin) }}
                      onSelect={(date) => {
                        if (!date) return;
                        const v = format(date, "yyyy-MM-dd");
                        setFormState((p) => {
                          const updated = { ...p, start: v };
                          validateField("start", v, updated);
                          validateField("end", p.end, updated);
                          return updated;
                        });
                      }}
                      initialFocus
                      className="p-3 pointer-events-auto" />
                    
                  </PopoverContent>
                </Popover>
                {fieldErrors.start &&
                <p className="text-[11px] text-[var(--color-error)] mt-1">
                    {fieldErrors.start}
                  </p>
                }
              </div>
              {!formState.allDay &&
              <div className="flex-1">
                  <label className={fieldLabelClass}><Trans i18nKey="dateTime.startTime" /></label>
                  <CustomTimeSelect
                  value={formState.startTime}
                  onChange={(val) =>
                  setFormState((p) => ({ ...p, startTime: val }))
                  }
                  options={timeOptions} />
                
                </div>
              }
            </div>

            <div className="flex gap-3">
              <div className="flex-1">
                <label className={fieldLabelClass}>
                  <Trans i18nKey="dateTime.endDate" /> <span className="text-[var(--color-error)]">*</span>
                </label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      type="button"
                      variant="outline"
                      className={cn(
                        "w-full h-10 justify-start text-left font-normal rounded-xl border bg-white px-3 text-sm hover:bg-[var(--color-workspace)]/40",
                        !formState.end && "text-muted-foreground",
                        fieldErrors.end ?
                        "border-[var(--color-error)]" :
                        "border-[var(--color-text-border)]"
                      )}>
                      
                      <CalendarIcon className="mr-2 h-4 w-4 text-[var(--color-text-secondary)]" />
                      {formState.end ?
                      format(new Date(formState.end), "PPP") :

                      <span><Trans i18nKey="dateTime.pickADate" /></span>
                      }
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto border-0 bg-transparent p-0 shadow-none" align="start">
                    <Calendar
                      mode="single"
                      selected={formState.end ? new Date(formState.end) : undefined}
                      disabled={{
                        before: formState.start ?
                        new Date(formState.start) :
                        new Date(todayMin)
                      }}
                      onSelect={(date) => {
                        if (!date) return;
                        const v = format(date, "yyyy-MM-dd");
                        setFormState((p) => {
                          const updated = { ...p, end: v };
                          validateField("end", v, updated);
                          return updated;
                        });
                      }}
                      initialFocus
                      className="p-3 pointer-events-auto" />
                    
                  </PopoverContent>
                </Popover>
                {fieldErrors.end &&
                <p className="text-[11px] text-[var(--color-error)] mt-1">
                    {fieldErrors.end}
                  </p>
                }
              </div>
              {!formState.allDay &&
              <div className="flex-1">
                  <label className={fieldLabelClass}><Trans i18nKey="dateTime.endTime" /></label>
                  <CustomTimeSelect
                  value={formState.endTime}
                  onChange={(val) =>
                  setFormState((p) => ({ ...p, endTime: val }))
                  }
                  options={timeOptions} />
                
                </div>
              }
            </div>

            <div>
              <label className={fieldLabelClass}>
                <Trans i18nKey="holidays.visibility" /> <span className="text-[var(--color-error)]">*</span>
              </label>
              <Select
                value={formState.visibility}
                onValueChange={(v) => {
                  setFormState((p) => ({
                    ...p,
                    visibility: v,
                    scopeValues: []
                  }));
                  setFieldErrors((p) => {
                    const next = { ...p };
                    delete next.scopeValues;
                    return next;
                  });

                  if (v === "Work Location" && locations.length === 0) {
                    fetchWorkLocations()
                    .then((data) =>
                    setLocations(Array.isArray(data) ? data : data?.data ?? [])
                    )
                    .catch(() => setLocations([]));
                  }
                  if (v === "Department" && departments.length === 0) {
                    fetchDepartments()
                    .then((data) =>
                    setDepartments(
                      Array.isArray(data) ? data : data?.data ?? []
                    )
                    )
                    .catch(() => setDepartments([]));
                  }
                }}>
                
                <SelectTrigger className="h-10 rounded-xl border-[var(--color-text-border)] bg-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Global"><Trans i18nKey="holidays.globalAllEmployees" /></SelectItem>
                  <SelectItem value="Department"><Trans i18nKey="holidays.departmentSpecific" /></SelectItem>
                  <SelectItem value="Work Location"><Trans i18nKey="holidays.workLocationSpecific" /></SelectItem>
                </SelectContent>
              </Select>
            </div>

            {formState.visibility !== "Global" &&
            <div>
                <label className={fieldLabelClass}>
                  <Trans i18nKey="holidays.selectTargets" />{formState.visibility}){" "}
                  <span className="text-[var(--color-error)]">*</span>
                </label>
                <div
                className={cn(
                  "rounded-xl border p-2.5 flex flex-col gap-1.5 max-h-[150px] overflow-y-auto",
                  fieldErrors.scopeValues ?
                  "border-[var(--color-error)]" :
                  "border-[var(--color-text-border)]"
                )}>
                
                  {(formState.visibility === "Department" ?
                departments :
                locations)
                .map((opt) => {
                  const id =
                  formState.visibility === "Department" ?
                  opt.departmentId :
                  opt.workLocationId;
                  const label =
                  formState.visibility === "Department" ?
                  opt.departmentName :
                  opt.workLocationName;
                  const isChecked = formState.scopeValues.includes(id);
                  return (
                    <label
                      key={id}
                      className="flex items-center gap-2 text-sm text-foreground cursor-pointer px-1 py-1 rounded-lg hover:bg-[var(--color-workspace)]/40">
                      
                        <input
                        type="radio"
                        name="scopeSelect"
                        checked={isChecked}
                        onChange={() => {
                          const updatedForm = {
                            ...formState,
                            scopeValues: [id]
                          };
                          setFormState(updatedForm);
                          validateField("scopeValues", [id], updatedForm);
                        }}
                        className="cursor-pointer" />
                      
                        {label}
                      </label>);

                })}
                </div>
                {fieldErrors.scopeValues &&
              <p className="text-[11px] text-[var(--color-error)] mt-1">
                    {fieldErrors.scopeValues}
                  </p>
              }
              </div>
            }

            <div>
              <label className={fieldLabelClass}>
                <Trans i18nKey="common.description2" />{" "}
                <span className="font-normal text-muted-foreground"><Trans i18nKey="common.optional" /></span>
              </label>
              <Textarea
                value={formState.description}
                onChange={(e) =>
                setFormState((p) => ({ ...p, description: e.target.value }))
                }
                onBlur={(e) => validateField("description", e.target.value)}
                placeholder={i18n.t("dateTime.addSpecificExecutionDetailsOrTimelineLinksDetails")}
                rows={3}
                className={cn(
                  "rounded-xl resize-none",
                  fieldErrors.description && "border-[var(--color-error)]"
                )} />
              
              {fieldErrors.description &&
              <p className="text-[11px] text-[var(--color-error)] mt-1">
                  {fieldErrors.description}
                </p>
              }
            </div>

            <div className="pt-4 border-t border-[var(--color-text-border)]/70 flex justify-end gap-3">
              <Button
                type="button"
                variant="outline"
                onClick={() => setIsPanelOpen(false)}
                className="h-11 px-6 rounded-xl border-[var(--color-primary)]/30 text-[var(--color-primary)] font-semibold text-sm hover:bg-[var(--color-workspace)]/40 bg-white">
                <Trans i18nKey="formActions.cancel" />
              
              </Button>
              <Button
                type="submit"
                disabled={!isFormValid}
                className="h-11 px-6 rounded-xl bg-[var(--color-primary)] text-white font-semibold text-sm hover:bg-[var(--color-accent)] transition shadow-[0_8px_20px_-8px_rgba(30,51,131,0.5)] min-w-[120px]">
                <Trans i18nKey="formActions.saveEvent" />
              
              </Button>
            </div>
          </form>
        </DialogPrimitive.Content>
      </DialogPortal>
    </Dialog>);

}
